import { produce } from "immer";
import { createStore } from "./createStore.ts";
import { getSearchParams, updateSearchParams } from "./url.ts";

export const createUrlStore = <
  State extends Record<string, unknown>,
  Actions extends Record<string, unknown>,
>(
  initialState: State,
  urlParams: {
    [K in keyof State]?: {
      paramName: string;
      serialize: (value: State[K]) => string;
      deserialize: (value: string) => State[K];
    };
  },
  create: Parameters<typeof createStore<State, Actions>>[1],
) => {
  // Function to extract state from URL params
  const getStateFromUrl = (): Partial<State> => {
    const searchParams = getSearchParams();
    return Object.entries(urlParams).reduce<Partial<State>>(
      (acc, [key, config]) => {
        const paramValue = searchParams.get(config.paramName);
        if (paramValue) {
          try {
            acc[key as keyof State] = config.deserialize(paramValue);
          } catch (e) {
            console.warn(
              `Failed to deserialize URL param ${config.paramName as string}:`,
              e,
            );
          }
        }
        return acc;
      },
      {},
    );
  };

  // Initialize state from URL params first
  const urlState = getStateFromUrl();

  const mergedInitialState = {
    ...initialState,
    ...urlState,
  };

  const store = createStore<State, Actions>(
    mergedInitialState,
    ({ set: _set, get }) => {
      let ignoreNextUrlChange = false;

      const set = (payload: Partial<State>) => {
        _set(payload);
        // XXX: To prevent infinite loop, we need to stop the popstate handler from updating the store again in case the store triggers a URL change.
        // To do so, we set a temporary flag `ignoreNextUrlChange` during the store update.
        ignoreNextUrlChange = true;
        const newSearchParams = getSearchParams();
        for (const [key, value] of Object.entries(payload)) {
          const config = urlParams[key as keyof State];
          if (config) {
            if (value !== undefined && value !== null) {
              newSearchParams.set(config.paramName, config.serialize(value));
            } else {
              newSearchParams.delete(config.paramName);
            }
          }
        }
        updateSearchParams(newSearchParams);
        // XXX: Allow the next URL change to update the store
        setTimeout(() => {
          ignoreNextUrlChange = false;
        }, 50);
      };

      // Handle URL changes (back/forward navigation)
      const handleUrlChange = () => {
        if (ignoreNextUrlChange) return;

        const currentUrlState = getStateFromUrl();
        if (Object.keys(currentUrlState).length > 0) _set(currentUrlState);
      };

      // Set up event listeners for URL changes
      if (typeof window !== "undefined") {
        window.addEventListener("popstate", handleUrlChange);
        window.addEventListener("hashchange", handleUrlChange);

        // Monitor URL changes via pushState/replaceState
        const originalPushState = window.history.pushState;
        const originalReplaceState = window.history.replaceState;

        window.history.pushState = function (...args) {
          originalPushState.apply(this, args);
          if (!ignoreNextUrlChange) handleUrlChange();
        };

        window.history.replaceState = function (...args) {
          originalReplaceState.apply(this, args);
          if (!ignoreNextUrlChange) handleUrlChange();
        };
      }

      return create({
        set,
        get,
        update: (fn) => set(produce(get(), fn)),
        setter: (field) => (value) =>
          // @ts-expect-error
          set({ [field]: value }),
      });
    },
  );

  return store;
};
